import React, { useState, useEffect, useMemo } from "react";
import { Settings, FolderOpen } from "lucide-react";
import useConfig from "../../demos/hooks/useConfig";
import { FolderSelectModal } from "../FolderSelectModal/FolderSelectModal";
import { useContentFolderTree } from "../FolderSelectModal/useContentFolderTree";
import { clipUrl, constructUrl, instanceRootFrom, stripInstance } from "./instancePaths";

interface VizQuerySetupModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type FolderField = "appFiles" | "svgFiles" | "jsonFiles";

/**
 * Where this frontend reads its content from: the application JSON, the
 * per-property JSON, and the SVG floor plans.
 *
 * Only that. Turning the same data into something the AI can answer questions
 * about is the AI Setup panel's job — it starts from these three folders, which
 * is why they are set here first.
 */
export const VizQuerySetupModal: React.FC<VizQuerySetupModalProps> = ({ isOpen, onClose }) => {
  const { config, updateConfig } = useConfig();

  const [appFiles, setAppFiles] = useState("");
  const [svgFiles, setSvgFiles] = useState("");
  const [jsonFiles, setJsonFiles] = useState("");

  const [isFolderSelectOpen, setIsFolderSelectOpen] = useState(false);
  const [activeField, setActiveField] = useState<FolderField | null>(null);

  const serverSubFolder = config.serverSubFolder || "";
  const instanceRoot = useMemo(() => instanceRootFrom(config), [config]);
  const { folderStructure, error } = useContentFolderTree(isOpen);

  useEffect(() => {
    if (!isOpen) return;
    setAppFiles(clipUrl(config.globalApplicationFilesLocation, instanceRoot));
    setSvgFiles(clipUrl(config.propertySVGFilesLocation, instanceRoot));
    setJsonFiles(clipUrl(config.propertyJSONFilesLocation, instanceRoot));
  }, [isOpen, config, instanceRoot]);

  const handleUpdate = () => {
    // The conversion server this deployment talks to has the last word on the
    // host: a value still carrying the build's http://localhost:3000 would come
    // back "Failed to fetch" however right the folder is.
    const server = config.ServerBackEndUrl;
    updateConfig({
      globalApplicationFilesLocation: constructUrl(
        appFiles,
        config.globalApplicationFilesLocation,
        instanceRoot,
        server
      ),
      propertySVGFilesLocation: constructUrl(
        svgFiles,
        config.propertySVGFilesLocation,
        instanceRoot,
        server
      ),
      propertyJSONFilesLocation: constructUrl(
        jsonFiles,
        config.propertyJSONFilesLocation,
        instanceRoot,
        server
      ),
    });
    onClose();
  };

  const handleFolderSelect = (path: string) => {
    // The picker returns a path relative to the content root, e.g.
    // "custom/uedge.com/json/application" — the leading "/content/" is gone.
    let formatted = stripInstance(path.startsWith("/") ? path : `/${path}`, instanceRoot);
    if (!formatted.startsWith("/")) formatted = `/${formatted}`;
    const finalPath = formatted.endsWith("/") ? formatted : `${formatted}/`;

    if (activeField === "appFiles") setAppFiles(finalPath);
    if (activeField === "svgFiles") setSvgFiles(finalPath);
    if (activeField === "jsonFiles") setJsonFiles(finalPath);
  };

  const fields: Array<{ key: FolderField; label: string; value: string; set: (v: string) => void }> = [
    { key: "appFiles", label: "Application Files:", value: appFiles, set: setAppFiles },
    { key: "svgFiles", label: "SVG Files:", value: svgFiles, set: setSvgFiles },
    { key: "jsonFiles", label: "JSON Content Files:", value: jsonFiles, set: setJsonFiles },
  ];

  return (
    <>
      <div className="flex flex-col flex-1 overflow-hidden">
        <div className="bg-white px-4 py-5 flex-1 overflow-y-auto">
          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 sm:mx-0 sm:h-10 sm:w-10">
              <Settings className="h-6 w-6 text-indigo-600" aria-hidden="true" />
            </div>
            <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
              <h3 className="text-base font-semibold leading-6 text-gray-900">
                VizQuery Folder Structure Setup
              </h3>
              {serverSubFolder && (
                <p className="mt-1 text-sm text-gray-500">
                  serverSubFolder: "{serverSubFolder}", (current settings)
                </p>
              )}

              <div className="mt-6 space-y-6">
                {fields.map((field) => (
                  <div key={field.key}>
                    <label className="block text-sm font-medium leading-6 text-gray-900">
                      {field.label}
                    </label>
                    <div className="mt-2 flex rounded-md shadow-sm">
                      <input
                        type="text"
                        className="block w-full rounded-none rounded-l-md border-0 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        value={field.value}
                        onChange={(e) => field.set(e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setActiveField(field.key);
                          setIsFolderSelectOpen(true);
                        }}
                        className="relative -ml-px inline-flex items-center gap-x-1.5 rounded-r-md px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 whitespace-nowrap"
                      >
                        <FolderOpen className="-ml-0.5 h-4 w-4 text-gray-400" aria-hidden="true" />
                        Select Folder
                      </button>
                    </div>
                  </div>
                ))}

                {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 px-4 py-3 flex flex-row-reverse border-t border-gray-200">
          <button
            type="button"
            className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 ml-3"
            onClick={handleUpdate}
          >
            Apply
          </button>
          <button
            type="button"
            className="inline-flex justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            onClick={onClose}
          >
            Cancel
          </button>
        </div>
      </div>

      <FolderSelectModal
        isOpen={isFolderSelectOpen}
        onClose={() => setIsFolderSelectOpen(false)}
        onSelect={handleFolderSelect}
        folderStructure={folderStructure}
        title={
          activeField === "appFiles"
            ? "Select Destination Folder - Application Files"
            : activeField === "svgFiles"
              ? "Select Destination Folder - SVG Files"
              : activeField === "jsonFiles"
                ? "Select Destination Folder - JSON Content Files"
                : "Select Destination Folder"
        }
      />
    </>
  );
};
